import { useSyncExternalStore } from 'react';

/**
 * Consentement aux cookies et traceurs de la régie.
 *
 * Trois catégories, pensées pour qu'on puisse y revenir et en ajouter :
 *   - essentiels  : session, sécurité, mémorisation de la connexion. Toujours actifs, non refusables
 *                   (ils sont strictement nécessaires au service demandé).
 *   - preferences : mémorisation locale des réglages d'affichage de la régie (thème, disposition,
 *                   configuration des decks…) quand l'opérateur les personnalise.
 *   - audience    : mesure d'audience / statistiques d'usage. AUCUNE mesure n'est active aujourd'hui ;
 *                   la catégorie existe pour qu'un futur outil ne puisse démarrer qu'avec un accord.
 *
 * Ne pas répondre vaut refus de tout ce qui n'est pas essentiel (comme sur le portail), et refuser
 * est aussi simple qu'accepter. Le choix est conservé 13 mois, puis redemandé. Il se change à tout
 * moment par « Gérer mes cookies » (pied de page) ou `openCookiePreferences()`.
 *
 * ⚠️ État actuel : ce module ENREGISTRE le choix et le met à disposition (`hasConsent`,
 * `useCookieConsent`). Les stockages existants de la régie (jeton, préférences d'affichage) ne
 * sont pas encore conditionnés par lui : à brancher catégorie par catégorie, plus tard.
 */

export type CookieCategory = 'essentiels' | 'preferences' | 'audience';

export interface CookieChoices {
  preferences : boolean;
  audience    : boolean;
}

export interface CookieConsentRecord extends CookieChoices {
  /** Version du texte et des catégories : un changement redemande le choix. */
  version   : number;
  decidedAt : string; // ISO
}

const STORAGE_KEY        = 'kleon.regie.cookieConsent';
export const CONSENT_VERSION    = 1;
const CONSENT_LIFETIME_MS = 13 * 30 * 24 * 3600 * 1000; // treize mois, comme sur le portail

export const OPEN_COOKIE_PREFERENCES_EVENT = 'regie:open-cookie-preferences';

const listeners = new Set<() => void>();

const readStored = (): CookieConsentRecord | null => {
  try
   {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw) as Partial<CookieConsentRecord>;
    if (rec.version !== CONSENT_VERSION) return null;
    const decidedAt = Date.parse(rec.decidedAt ?? '');
    if (!Number.isFinite(decidedAt) || Date.now() - decidedAt > CONSENT_LIFETIME_MS) return null;
    return { version: CONSENT_VERSION, decidedAt: rec.decidedAt as string, preferences: !!rec.preferences, audience: !!rec.audience };
   }
  catch { return null; }
};

// Instantané stable : useSyncExternalStore exige la même référence tant que rien ne change.
let current: CookieConsentRecord | null = readStored();

const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };

/** Enregistre un choix (remplace le précédent) et prévient les composants abonnés. */
export const saveCookieConsent = (choices: CookieChoices): void => {
  current = { version: CONSENT_VERSION, decidedAt: new Date().toISOString(), preferences: choices.preferences, audience: choices.audience };
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current)); } catch { /* stockage indisponible : le choix vaut pour cette session */ }
  listeners.forEach(cb => cb());
};

export const acceptAllCookies = (): void => saveCookieConsent({ preferences: true,  audience: true  });
export const refuseAllCookies = (): void => saveCookieConsent({ preferences: false, audience: false });

/** null : aucun choix valide (jamais répondu, expiré, ou texte changé) → le bandeau s'affiche. */
export const getCookieConsent = (): CookieConsentRecord | null => current;

export const useCookieConsent = (): CookieConsentRecord | null => useSyncExternalStore(subscribe, () => current);

/** Le visiteur a-t-il accepté cette catégorie ? Les essentiels : toujours ; le reste : seulement sur accord explicite. */
export const hasConsent = (category: CookieCategory): boolean =>
  category === 'essentiels' ? true : !!current && current[category];

/** Rouvre la fenêtre des préférences (lien « Gérer mes cookies »). */
export const openCookiePreferences = (): void => { window.dispatchEvent(new Event(OPEN_COOKIE_PREFERENCES_EVENT)); };
